import { LoadingOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, Input, notification, Row, Select } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { searchEspeciesByProduto } from '../../../../helpers/especies';
import { filterOption } from '../../../../helpers/form';
import { formatarCnpjCpfIMask } from '../../../../helpers/formatador';
import { getPagadores } from '../../../../helpers/pagadores';
import { searchProdutosByClientePagador } from '../../../../helpers/produtos';
import { useAuth } from '../../../../security/authContext';
import apiShipper from '../../../../services/apiShipper';
import DirecionamentoEnum from '../../../Demanda/Enum/DirecionamentoEnum';
import { Label } from './styles';

export default function DadosGerais({
  dadosPagador,
  setDadosPagador,
  form,
  disableField,
  isMarketplace,
  dadosLote,
  editingLote,
}) {
  const direcionamentoOption = DirecionamentoEnum.getTiposLabel();

  const { setFieldsValue, validateFields, getFieldValue } = form;
  const { user } = useAuth();
  const [apiProdutos, setProdutos] = useState([]);
  const [apiClientes, setApiClientes] = useState([]);
  const [apiEspecies, setApiEspecies] = useState([]);
  const [
    apiFrotistaOrGrupoFrotistas,
    setApiFrotistaOrGrupoFrotistas,
  ] = useState([]);
  const [disabledDataFim, setDisabledDataFim] = useState(moment());
  const [loadedClientes, setLoadedClientes] = useState(false);
  const [loadingEspecies, setLoadingEspecies] = useState(false);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  const [disabledEspecie, setDisabledEspecie] = useState(true);
  const [disabledProduto, setDisabledProduto] = useState(true);
  const [direcionamentoSelected, setDirecionamentoSelected] = useState(
    direcionamentoOption[0]
  );
  const [loadingDirecionamento, setLoadingDirecionamento] = useState(false);

  const handleSelectPagador = async id => {
    const pagador = apiClientes.find(apiCliente => apiCliente.id === id);

    setFieldsValue({
      produto: null,
      especie: null,
    });

    setDadosPagador(pagador);
    setDisabledProduto(false);
    setDisabledEspecie(true);
  };

  const handleTipoDirecionamento = (
    callbackFrotista,
    callbackGrupoFrotista,
    tipoDirecionamento
  ) => {
    if (tipoDirecionamento === DirecionamentoEnum.FROTISTA) {
      return callbackFrotista;
    }
    if (tipoDirecionamento === DirecionamentoEnum.GRUPO_FROTISTA) {
      return callbackGrupoFrotista;
    }
    return null;
  };

  const handleSetProdutoDescricao = async (idProduto, descricaoProduto) => {
    if (!idProduto) return;

    await searchEspeciesByProduto(
      dadosPagador.id,
      idProduto,
      setApiEspecies,
      setLoadingEspecies
    );

    setFieldsValue({
      descricaoProduto,
    });
  };

  const handleDirecionamento = async index => {
    setDirecionamentoSelected(direcionamentoOption[index]);
    setApiFrotistaOrGrupoFrotistas([]);

    validateFields();
  };

  const handleSearchGrupoFrotistas = async value => {
    if (!value || value.length < 3) return;

    setLoadingDirecionamento(true);

    try {
      const { data } = await apiShipper.get(
        `/grupo-frotista/${user.cliente_tmov_id}`,
        {
          params: {
            nome: value,
          },
        }
      );

      setApiFrotistaOrGrupoFrotistas(data);
    } catch (err) {
      notification.error({
        message: 'Ops, não foi possível recuperar o grupo frotistas',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    } finally {
      setLoadingDirecionamento(false);
    }
  };

  const handleSearchFrotistas = async value => {
    if (!value || value.length < 3) return;

    setLoadingDirecionamento(true);
    try {
      const { data } = await apiShipper.get('/frotista/ativo', {
        params: {
          filtro: value,
        },
      });

      setApiFrotistaOrGrupoFrotistas(data);
    } catch (err) {
      notification.error({
        message: 'Ops, não foi possível recuperar os frotistas',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    } finally {
      setLoadingDirecionamento(false);
    }
  };

  const onSearchFrotistas = useDebouncedCallback(async value => {
    handleSearchFrotistas(value);
  }, 1000);

  const onSearchGrupoFrotistas = useDebouncedCallback(async value => {
    handleSearchGrupoFrotistas(value);
  }, 1000);

  const selectOptionFrotista = frotista => (
    <Select.Option
      key={frotista.id}
      value={`${frotista.razao_social}`}
      cnpj={frotista.cnpj}
    >
      {`${frotista.razao_social}`}
      <br />
      {`${frotista.nome_fantasia}`}
      <br />
      {`${formatarCnpjCpfIMask(frotista.cnpj)}`}
    </Select.Option>
  );

  const selectOptionGrupoFrotista = grupoFrotista => (
    <Select.Option
      key={grupoFrotista.idGrupoFrotista}
      value={grupoFrotista.nome}
    >
      {`${grupoFrotista.nome}`}
    </Select.Option>
  );

  const handleSelectFrotista = async (value, { cnpj }) => {
    setFieldsValue({
      [`direcionadoPara${DirecionamentoEnum.FROTISTA}`]: `${value} - ${formatarCnpjCpfIMask(
        cnpj
      )}`,
    });

    try {
      const { data } = await apiShipper.get(`/proprietario/${cnpj}`);

      setFieldsValue({
        idProprietario: data.IdProprietario,
      });
    } catch ({ response }) {
      // TODO
      notification.error({
        message: 'Ops, não foi possível selecionar o frotista',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    }
  };

  const handleSelectGrupoFrotista = async (
    _value,
    { key: idGrupoFrotista }
  ) => {
    setFieldsValue({
      idGrupoFrotista,
    });
  };

  useEffect(() => {
    async function onInit() {
      const pagadores = await getPagadores(user);
      if (pagadores) setApiClientes(pagadores);
      setLoadedClientes(true);
    }

    onInit();
  }, [user]);

  useEffect(() => {
    setFieldsValue({
      especie: apiEspecies.length === 1 ? apiEspecies[0].IdEspecie : null,
    });
    setDisabledEspecie(apiEspecies.length <= 1);
    if (apiEspecies.length === 1) validateFields();
  }, [apiEspecies]);

  useEffect(() => {
    async function getProdutos() {
      if (!dadosPagador.id) return;
      const { produto, produtoNome, especie, contrato } = dadosLote;
      await searchProdutosByClientePagador(
        dadosPagador.id,
        setProdutos,
        setLoadingProdutos
      );
      await handleSetProdutoDescricao(produto, produtoNome);

      if (especie) {
        setFieldsValue({
          especie,
        });
      }

      if (contrato) {
        setDisabledProduto(true);
        setDisabledEspecie(true);
      }
    }

    getProdutos();
  }, [dadosPagador]);

  useEffect(() => {
    async function onEditingLote() {
      if (Object.values(dadosLote).length === 0) return;
      const {
        dadoPagador,
        contrato,
        direcionamento,
        nomeFantasiaProprietario,
        nomeGrupoFrotista,
        cnpjCpfProprietario,
      } = dadosLote;

      if (contrato) {
        setApiClientes([dadoPagador]);
      } else {
        const existePagador = apiClientes.some(
          cliente => cliente.id === dadoPagador.id
        );

        if (!existePagador) setApiClientes([...apiClientes, dadoPagador]);
      }
      setFieldsValue({
        [`direcionadoPara${direcionamento}`]:
          direcionamento === DirecionamentoEnum.FROTISTA
            ? `${nomeFantasiaProprietario} - ${formatarCnpjCpfIMask(
                cnpjCpfProprietario
              )}`
            : nomeGrupoFrotista,
      });

      setDirecionamentoSelected(DirecionamentoEnum.getTipo(direcionamento));

      setDisabledProduto(false);
      setDisabledEspecie(false);

      validateFields();
    }

    onEditingLote();
  }, [dadosLote, loadedClientes]);

  return (
    <>
      {editingLote && dadosLote.idLote && (
        <Row gutter={[12, 12]}>
          <Col>
            <Label>{`Lote#${dadosLote.idLote} - ${dadosLote.status}`}</Label>
          </Col>
        </Row>
      )}
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item
            name="cnpjPagador"
            label="Cliente Pagador"
            rules={[
              { required: true, message: 'Por favor, selecione um CNPJ!' },
            ]}
          >
            <Select
              aria-label="cnpjPagador"
              showSearch
              showArrow={false}
              placeholder="Selecione um CPNJ"
              filterOption={(input, { title: titleOption }) =>
                filterOption(input, titleOption, 0)
              }
              showAction={['click']}
              onChange={handleSelectPagador}
              disabled={disableField || !isMarketplace}
            >
              {apiClientes.map(dado => (
                <Select.Option
                  key={dado.id}
                  value={dado.id}
                  title={`${dado.razao_social} - ${formatarCnpjCpfIMask(
                    dado.cnpj
                  )}`}
                >
                  {dado.razao_social}
                  &nbsp; - &nbsp;
                  {formatarCnpjCpfIMask(dado.cnpj)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Row gutter={12}>
            <Col span={11}>
              <Form.Item
                name="dataInicio"
                label="Data inicial"
                rules={[
                  {
                    type: 'date',
                    required: true,
                    message: 'Por favor selecione uma data inicial!',
                  },
                ]}
                initialValue={moment()}
              >
                <DatePicker
                  aria-label="dataInicio"
                  disabled={disableField || !isMarketplace}
                  className="total-width"
                  format="DD/MM/YYYY"
                  placeholder={moment().format('DD/MM/YYYY')}
                  disabledDate={d => !d || d.isBefore(moment().add(-1, 'days'))}
                />
              </Form.Item>
            </Col>
            <Col span={12} offset={1}>
              <Form.Item name="dataFim" label="Data final">
                <DatePicker
                  disabled={disableField || !isMarketplace}
                  className="total-width"
                  format="DD/MM/YYYY"
                  placeholder={moment()
                    .add(10, 'days')
                    .format('DD/MM/YYYY')}
                  disabledDate={d => !d || d.isBefore(disabledDataFim)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item
            name="produto"
            label="Produtos"
            rules={[
              { required: true, message: 'Por favor, insira um produto!' },
            ]}
          >
            <Select
              aria-label="produto"
              disabled={disabledProduto || !isMarketplace}
              showSearch
              showArrow={false}
              placeholder="Ex: SOJA"
              filterOption={false}
              showAction={['click']}
              notFoundContent={loadingProdutos ? <LoadingOutlined /> : null}
              onChange={(value, option) =>
                handleSetProdutoDescricao(value, option.key)
              }
              loading={loadingProdutos}
            >
              {apiProdutos.map(dado => (
                <Select.Option key={dado.Descricao} value={dado.IdProduto}>
                  {`${dado.IdProduto} - ${dado.Descricao}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Form.Item
            name="especie"
            label="Espécie"
            rules={[
              { required: true, message: 'Por favor, insira uma espécie!' },
            ]}
          >
            <Select
              aria-label="especie"
              disabled={disabledEspecie || !isMarketplace}
              showSearch
              showArrow={false}
              placeholder="Ex: BALDE"
              filterOption={(input, { title: titleOption }) =>
                filterOption(input, titleOption, 0)
              }
              showAction={['click']}
              notFoundContent={loadingEspecies ? <LoadingOutlined /> : null}
              loading={loadingEspecies}
            >
              {apiEspecies.map(dado => (
                <Select.Option
                  key={dado.IdEspecie}
                  title={dado.Descricao}
                  value={dado.IdEspecie}
                >
                  {dado.Descricao}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item name="direcionamento" label="Direcionamento para">
            <Select
              defaultValue={direcionamentoOption[0].value}
              aria-label="direcionamento"
              disabled={!isMarketplace}
              showAction={['click']}
              onChange={(_value, option) => handleDirecionamento(option.index)}
            >
              {direcionamentoOption.map((option, index) => (
                <Select.Option
                  key={option.label}
                  index={index}
                  value={option.value}
                >
                  {`${option.label}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {direcionamentoSelected.value !== DirecionamentoEnum.PUBLICO && (
          <Col span={12} offset={1}>
            <Form.Item
              name={`direcionadoPara${direcionamentoSelected.value}`}
              label={`Buscar ${direcionamentoSelected.label}`}
              rules={[
                {
                  required: true,
                  message: `Por favor, insira um ${direcionamentoSelected.label}`,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                showArrow={false}
                placeholder={handleTipoDirecionamento(
                  'Digite o CNPJ, razão social ou nome fantasia',
                  'Digite o nome do grupo',
                  direcionamentoSelected.value
                )}
                filterOption={false}
                showAction={['click']}
                onSelect={handleTipoDirecionamento(
                  handleSelectFrotista,
                  handleSelectGrupoFrotista,
                  direcionamentoSelected.value
                )}
                notFoundContent={
                  loadingDirecionamento ? <LoadingOutlined /> : null
                }
                onSearch={handleTipoDirecionamento(
                  onSearchFrotistas,
                  onSearchGrupoFrotistas,
                  direcionamentoSelected.value
                )}
                loading={loadingDirecionamento}
              >
                {// eslint-disable-next-line no-confusing-arrow
                apiFrotistaOrGrupoFrotistas.map(dado =>
                  direcionamentoSelected.value === DirecionamentoEnum.FROTISTA
                    ? selectOptionFrotista(dado)
                    : selectOptionGrupoFrotista(dado)
                )}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea
              disabled={disableField || !isMarketplace}
              placeholder="Descreva seu roteiro aqui (opcional)"
              className="total-width"
              maxLength={2000}
              showCount
              rows={4}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

DadosGerais.propTypes = {
  dadosPagador: PropTypes.shape({
    id: PropTypes.number,
    cnpj: PropTypes.string,
  }).isRequired,
  setDadosPagador: PropTypes.func.isRequired,
  form: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  ).isRequired,
  disableField: PropTypes.bool.isRequired,
  isMarketplace: PropTypes.bool.isRequired,
  dadosLote: PropTypes.objectOf(PropTypes.any).isRequired,
  editingLote: PropTypes.bool.isRequired,
};
